import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useAuth } from './AuthContext';
import { 
  Package, ArrowRight, Check, Truck, Palette, 
  Ruler, Zap, Shield, Users, LogIn, Search, Settings, LogOut,
  ChevronDown
} from 'lucide-react';
import ResistanceIndicator from './ResistanceIndicator';
import MATERIAL_META from './materialMeta';
import CustomerMenu from './CustomerMenu';
import QuickQuote from './QuickQuote';

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-border/60 rounded-lg bg-white overflow-hidden" data-testid="faq-item">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/20 transition-colors"
      >
        <span className="font-semibold text-primary text-sm sm:text-base pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0 text-sm text-muted-foreground leading-relaxed animate-fadeIn">
          {answer}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { user, loading, login, logout } = useAuth();

  const features = [
    {
      icon: Ruler,
      title: 'Medidas Personalizadas',
      description: 'Crea cajas a la medida exacta de tu producto'
    },
    {
      icon: Palette,
      title: 'Impresión Digital CMYK',
      description: 'Colores vibrantes sin mínimos de orden'
    },
    {
      icon: Zap,
      title: 'Entrega en 5 días',
      description: 'Producción rápida con calidad garantizada'
    },
    {
      icon: Shield,
      title: 'Cartón de Alta Calidad',
      description: 'Material resistente para proteger tu producto'
    }
  ];

  const steps = [
    { number: '01', title: 'Configura', description: 'Elige dimensiones, material y cantidad' },
    { number: '02', title: 'Diseña', description: 'Sube tu artwork o usa nuestras plantillas' },
    { number: '03', title: 'Cotiza', description: 'Precio instantáneo sin sorpresas' },
    { number: '04', title: 'Recibe', description: 'Entrega en tu puerta en 5 días' },
  ];

  return (
    <div className="min-h-screen bg-background font-manrope">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#como-funciona" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Cómo funciona
            </a>
            <a href="#materiales" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Materiales
            </a>
            <a href="#ventajas" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Ventajas
            </a>
            <a href="#faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              FAQ
            </a>
          </nav>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/rastreo')}
              className="gap-2 text-muted-foreground hover:text-primary"
              data-testid="header-track-btn"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Rastrear pedido</span>
            </Button>

            {!loading && !user && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => login()}
                className="gap-2"
                data-testid="header-login-btn"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Iniciar sesión</span>
              </Button>
            )}

            {!loading && user && <CustomerMenu />}

            <Button 
              onClick={() => navigate('/configurar')} 
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-6"
              data-testid="header-cta-btn"
            >
              Crear mi caja
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-muted/30 to-secondary/20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-border/50 text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Sin mínimo de pedido
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-primary tracking-tight leading-tight">
                Cajas de cartón
                <span className="block text-accent">a tu medida</span>
              </h1>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                Diseña y ordena cajas de cartón corrugado con impresión personalizada. 
                Sin mínimos, entrega en 5 días, calidad premium.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/configurar')}
                  className="h-14 px-8 text-lg bg-accent hover:bg-accent/90 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-cta-btn"
                >
                  Comenzar ahora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="h-14 px-8 text-lg rounded-full"
                  onClick={() => document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Ver cómo funciona
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-secondary border-2 border-white flex items-center justify-center">
                      <Users className="w-5 h-5 text-muted-foreground" />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="font-semibold text-primary">+500 clientes</p>
                  <p className="text-sm text-muted-foreground">confían en KAHA</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative max-w-lg mx-auto">
                {/* Instagram button - top right */}
                <a
                  href="https://www.instagram.com/kahamx/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-4 right-4 z-10 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white hover:scale-105 transition-all duration-200 border border-white/50"
                  aria-label="Instagram KAHA"
                  data-testid="btn-instagram"
                >
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </a>

                <img 
                  src="https://customer-assets.emergentagent.com/job_a5a63ab2-e6ce-4f04-9a31-0beec38ff3e2/artifacts/aay6c9ds_Caja%20KAHA.png"
                  alt="Caja de cartón corrugado KAHA - tu marca"
                  className="w-full h-auto rounded-2xl shadow-floating object-cover"
                  style={{ maxHeight: '440px' }}
                />
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-white px-4 py-3 rounded-xl shadow-lg border border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Palette className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm">Impresión digital</p>
                      <p className="text-xs text-muted-foreground">CMYK alta calidad</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white px-4 py-3 rounded-xl shadow-lg border border-border/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                    <Truck className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary text-sm">Entrega rápida</p>
                    <p className="text-xs text-muted-foreground">5 días hábiles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Quote Widget */}
      <QuickQuote />

      {/* How it Works */}
      <section id="como-funciona" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
              Cómo funciona
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              En solo 4 pasos tendrás tus cajas personalizadas listas para enviar
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={step.number} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+32px)] w-[calc(100%-64px)] h-0.5 bg-border" />
                )}
                <div className="text-center space-y-4 relative z-10">
                  <div className="w-16 h-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center border-4 border-white">
                    <span className="text-2xl font-bold text-accent">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-primary">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="ventajas" className="py-20 lg:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
              ¿Por qué KAHA?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Somos la opción más moderna y flexible para tu empaque personalizado
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <Card key={feature.title} className="bg-white border-border/50 hover:border-accent/30 transition-colors duration-300">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-14 h-14 mx-auto bg-accent/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section id="materiales" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
              Nuestros materiales
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Elige el material ideal según el uso de tu producto
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(MATERIAL_META).map(([name, meta]) => (
              <div key={name} className="group bg-white border border-border/60 rounded-xl overflow-hidden hover:shadow-lg hover:border-accent/30 transition-all duration-300">
                <div className="aspect-square overflow-hidden bg-muted/20">
                  <img
                    src={meta.image}
                    alt={name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 space-y-3">
                  <h3 className="font-bold text-primary">{name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{meta.shortDesc}</p>
                  <ResistanceIndicator level={meta.resistance} size="sm" />
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button
              onClick={() => navigate('/configurar')}
              variant="outline"
              className="rounded-full px-8 h-12 text-base"
              data-testid="materials-cta-btn"
            >
              Configurar mi caja
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-32 bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
              Preguntas frecuentes
            </h2>
          </div>

          <div className="space-y-3" data-testid="faq-section">
            {[
              {
                q: '¿Qué tipo de cartón necesito?',
                a: 'Depende del peso de tu producto. Para envíos ligeros recomendamos sencillo, y para productos pesados o frágiles, doble.',
              },
              {
                q: '¿Las medidas son internas o externas?',
                a: 'Las medidas que ingresas son externas de la caja.',
              },
              {
                q: '¿Cómo preparo mi archivo?',
                a: 'Puedes subir tu diseño en PDF, PNG o CDR. Nuestro sistema lo adapta automáticamente al área imprimible.',
              },
              {
                q: '¿Cuánto tarda la producción?',
                a: 'El tiempo estándar de producción es de 5 días hábiles.',
              },
              {
                q: '¿Hacen envíos a todo México?',
                a: 'Sí, realizamos envíos a toda la República Mexicana.',
              },
            ].map((item) => (
              <FaqItem key={item.q} question={item.q} answer={item.a} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            ¿Listo para crear tu empaque perfecto?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Comienza a configurar tu caja ahora. Cotización instantánea, sin compromisos.
          </p>
          <Button 
            onClick={() => navigate('/configurar')}
            className="h-14 px-10 text-lg bg-white text-primary hover:bg-white/90 rounded-full shadow-lg"
            data-testid="cta-bottom-btn"
          >
            Crear mi caja
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-accent" />
              Sin mínimo de pedido
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-accent" />
              Cotización instantánea
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-accent" />
              Entrega en 5 días
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-accent" />
              <span className="text-lg font-bold text-primary">KAHA</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 KAHA Packaging. Hecho en México con ❤️
            </p>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <a href="/terminos" className="hover:text-primary transition-colors">Términos</a>
              <a href="/privacidad" className="hover:text-primary transition-colors">Privacidad</a>
              <a href="/contacto" className="hover:text-primary transition-colors">Contacto</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
